import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { API, BACKEND_HOST, SITE_URL } from "@/api";
import { Button, Card, CardBody, CardHeader, Col, Container, Row } from "@/Components/ui";
import { dialogService } from "@/utils/dialogService";

type PackageOption = {
  id: number;
  name?: string;
  상품명?: string;
};

type PrivateOfferTarget = {
  id?: number;
  phone_num: string;
  memo?: string;
};

type PrivateOfferItem = {
  id?: number;
  package: number;
  package_name?: string;
  final_price: number;
  weekday_price?: number;
  weekend_holiday_price?: number;
  priority: number;
  state: "active" | "paused";
  max_redemptions?: number;
};

type PrivateOffer = {
  id: number;
  package: number;
  package_name?: string;
  title: string;
  guide_message?: string;
  token: string;
  state: "active" | "paused";
  starts_at?: string | null;
  ends_at?: string | null;
  reservation_start_date?: string | null;
  reservation_end_date?: string | null;
  final_price: number;
  allow_without_target?: boolean;
  max_redemptions: number;
  per_user_limit: number;
  items?: PrivateOfferItem[];
  targets?: PrivateOfferTarget[];
  redemption_count?: number;
};

type PrivateOfferItemForm = {
  package: string;
  weekday_price: string;
  weekend_holiday_price: string;
  priority: string;
  state: "active" | "paused";
  max_redemptions: string;
};

type PrivateOfferForm = {
  id: number;
  package: string;
  title: string;
  guide_message: string;
  state: "active" | "paused";
  starts_at: string;
  ends_at: string;
  reservation_start_date: string;
  reservation_end_date: string;
  final_price: string;
  allow_without_target: boolean;
  max_redemptions: string;
  per_user_limit: string;
  target_phone_nums: string;
  items: PrivateOfferItemForm[];
};

const emptyForm: PrivateOfferForm = {
  id: 0,
  package: "",
  title: "",
  guide_message: "",
  state: "active",
  starts_at: "",
  ends_at: "",
  reservation_start_date: "",
  reservation_end_date: "",
  final_price: "",
  allow_without_target: false,
  max_redemptions: "",
  per_user_limit: "1",
  target_phone_nums: "",
  items: [{ package: "", weekday_price: "", weekend_holiday_price: "", priority: "0", state: "active", max_redemptions: "" }],
};

const toDateTimeLocal = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 16);
};

const fromDateTimeLocal = (value: string) => value ? new Date(value).toISOString() : null;

const formatPrice = (value: number | string | null | undefined) => Number(value || 0).toLocaleString();

const buildOfferUrl = (token: string) => {
  const url = new URL(`/private-package/${token}`, SITE_URL);
  return url.toString();
};

export default function PrivateOffersPage() {
  const [offers, setOffers] = useState<PrivateOffer[]>([]);
  const [packages, setPackages] = useState<PackageOption[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const selectedOffer = useMemo(() => offers.find((offer) => offer.id === form.id) || null, [form.id, offers]);

  const fetchData = useCallback(async () => {
    const [offersResponse, packagesResponse] = await Promise.all([
      API.get("/package/private-offers/"),
      API.get(`${BACKEND_HOST}/package/items/?include_hidden=true`),
    ]);
    setOffers(offersResponse.data || []);
    setPackages(packagesResponse.data || []);
  }, []);

  useEffect(() => {
    fetchData().catch((error) => {
      console.error(error);
      dialogService.handleAlert("Private Offer 목록을 불러오지 못했습니다.", { force: "error" });
    });
  }, [fetchData]);

  const resetForm = () => setForm(emptyForm);

  const editOffer = (offer: PrivateOffer) => {
    const offerItems = offer.items?.length
      ? offer.items.map((item) => ({
          package: String(item.package || ""),
          weekday_price: String(item.weekday_price || item.final_price || ""),
          weekend_holiday_price: String(item.weekend_holiday_price || item.final_price || ""),
          priority: String(item.priority || 0),
          state: item.state || "active",
          max_redemptions: item.max_redemptions ? String(item.max_redemptions) : "",
        }))
      : [{ package: String(offer.package || ""), weekday_price: String(offer.final_price || ""), weekend_holiday_price: String(offer.final_price || ""), priority: "0", state: "active", max_redemptions: "" }];
    setForm({
      id: offer.id,
      package: String(offer.package || ""),
      title: offer.title || "",
      guide_message: offer.guide_message || "",
      state: offer.state || "active",
      starts_at: toDateTimeLocal(offer.starts_at),
      ends_at: toDateTimeLocal(offer.ends_at),
      reservation_start_date: offer.reservation_start_date || "",
      reservation_end_date: offer.reservation_end_date || "",
      final_price: String(offer.final_price || ""),
      allow_without_target: Boolean(offer.allow_without_target),
      max_redemptions: offer.max_redemptions ? String(offer.max_redemptions) : "",
      per_user_limit: offer.per_user_limit ? String(offer.per_user_limit) : "1",
      target_phone_nums: (offer.targets || []).map((target) => target.phone_num).join("\n"),
      items: offerItems,
    });
  };

  const copyOfferLink = async (token: string) => {
    const url = buildOfferUrl(token);
    await navigator.clipboard.writeText(url);
    await dialogService.handleAlert("링크가 복사되었습니다.");
  };

  const saveOffer = async () => {
    const items = form.items
      .filter((item) => item.package && item.weekday_price && item.weekend_holiday_price)
      .map((item, index) => ({
        package: Number(item.package),
        final_price: Number(item.weekday_price || 0),
        weekday_price: Number(item.weekday_price || 0),
        weekend_holiday_price: Number(item.weekend_holiday_price || 0),
        priority: Number(item.priority || index),
        state: item.state || "active",
        max_redemptions: Number(item.max_redemptions || 0),
      }));
    if (!form.title || !items.length) {
      await dialogService.handleAlert("제목과 상품별 평일가, 주말·공휴일가를 입력해 주세요.", { force: "error" });
      return;
    }
    if (form.reservation_start_date && form.reservation_end_date && form.reservation_end_date < form.reservation_start_date) {
      await dialogService.handleAlert("예약 종료일은 예약 시작일보다 빠를 수 없습니다.", { force: "error" });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        package: items[0].package,
        title: form.title,
        guide_message: form.guide_message,
        state: form.state,
        starts_at: fromDateTimeLocal(form.starts_at),
        ends_at: fromDateTimeLocal(form.ends_at),
        reservation_start_date: form.reservation_start_date || null,
        reservation_end_date: form.reservation_end_date || null,
        final_price: items[0].final_price,
        allow_without_target: form.allow_without_target,
        max_redemptions: Number(form.max_redemptions || 0),
        per_user_limit: Number(form.per_user_limit || 1),
        target_phone_nums: form.allow_without_target ? "" : form.target_phone_nums,
        items,
      };
      if (form.id) {
        await API.patch(`/package/private-offers/${form.id}/`, payload);
        await API.post(`/package/private-offers/${form.id}/targets/replace/`, {
          target_phone_nums: form.allow_without_target ? "" : form.target_phone_nums,
        });
      } else {
        await API.post("/package/private-offers/", payload);
      }
      await fetchData();
      resetForm();
      await dialogService.handleAlert("저장되었습니다.");
    } catch (error: any) {
      await dialogService.handleAlert(error?.response?.data?.error || error?.message || "저장에 실패했습니다.", { force: "error" });
    } finally {
      setLoading(false);
    }
  };

  const toggleState = async (offer: PrivateOffer) => {
    const nextState = offer.state === "active" ? "paused" : "active";
    try {
      await API.patch(`/package/private-offers/${offer.id}/`, { state: nextState });
      await fetchData();
    } catch (error: any) {
      await dialogService.handleAlert(error?.response?.data?.error || "상태 변경에 실패했습니다.", { force: "error" });
    }
  };

  const updateItem = (index: number, field: keyof PrivateOfferItemForm, value: string) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        const nextItem = { ...item };
        if (field === "state") {
          nextItem.state = value as PrivateOfferItemForm["state"];
        } else {
          nextItem[field] = value;
        }
        return nextItem;
      }),
    }));
  };

  const addItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, { package: "", weekday_price: "", weekend_holiday_price: "", priority: String(prev.items.length), state: "active", max_redemptions: "" }],
    }));
  };

  const removeItem = (index: number) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.length <= 1 ? prev.items : prev.items.filter((_item, itemIndex) => itemIndex !== index),
    }));
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Private Offer" pageTitle="예약" />
        <Row>
          <Col xl={8}>
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <h4 className="card-title mb-0">Private Offer 목록</h4>
                <Button color="success" onClick={resetForm}>
                  <i className="ri-add-line align-bottom me-1" /> 생성
                </Button>
              </CardHeader>
              <CardBody>
                <div className="table-responsive">
                  <table className="table align-middle table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>제목</th>
                        <th>상품</th>
                        <th>최종가</th>
                        <th>상태</th>
                        <th>사용</th>
                        <th>관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {offers.map((offer) => (
                        <tr key={offer.id}>
                          <td className="fw-semibold">{offer.title}</td>
                          <td>{offer.items?.map((item) => item.package_name || item.package).join(", ") || offer.package_name || offer.package}</td>
                          <td>{offer.items?.length ? `${offer.items.length}개 상품` : `${formatPrice(offer.final_price)}원`}</td>
                          <td>
                            <span className={`badge ${offer.state === "active" ? "bg-success" : "bg-secondary"}`}>
                              {offer.state === "active" ? "활성" : "중지"}
                            </span>
                          </td>
                          <td>{offer.redemption_count || 0} / {offer.max_redemptions || "-"}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              <Button size="sm" color="info" onClick={() => editOffer(offer)}>수정</Button>
                              <Button size="sm" color={offer.state === "active" ? "warning" : "success"} onClick={() => toggleState(offer)}>
                                {offer.state === "active" ? "중지" : "활성"}
                              </Button>
                              <Button size="sm" color="secondary" onClick={() => copyOfferLink(offer.token)}>링크 복사</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {!offers.length ? (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4">등록된 Private Offer가 없습니다.</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl={4}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">{selectedOffer ? "Private Offer 수정" : "Private Offer 생성"}</h4>
              </CardHeader>
              <CardBody>
                <div className="mb-3">
                  <label className="form-label">제목</label>
                  <input className="form-control" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
                </div>
                <div className="mb-3">
                  <label className="form-label">상단 안내 문구</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={form.guide_message}
                    onChange={(event) => setForm({ ...form, guide_message: event.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <label className="form-label mb-0">예약상품 / 평일가 / 주말·공휴일가</label>
                    <Button size="sm" color="secondary" onClick={addItem}>상품 추가</Button>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {form.items.map((item, index) => (
                      <div key={`offer-item-${index}`} className="rounded border bg-light p-2">
                        <div className="mb-2">
                          <select className="form-select" value={item.package} onChange={(event) => updateItem(index, "package", event.target.value)}>
                            <option value="">상품 선택</option>
                            {packages.map((packageItem) => (
                              <option key={packageItem.id} value={packageItem.id}>{packageItem.상품명 || packageItem.name || `상품 ${packageItem.id}`}</option>
                            ))}
                          </select>
                        </div>
                        <Row>
                          <Col md={4}>
                            <input className="form-control" type="number" min={0} placeholder="평일가" value={item.weekday_price} onChange={(event) => updateItem(index, "weekday_price", event.target.value)} />
                          </Col>
                          <Col md={4}>
                            <input className="form-control" type="number" min={0} placeholder="주말·공휴일가" value={item.weekend_holiday_price} onChange={(event) => updateItem(index, "weekend_holiday_price", event.target.value)} />
                          </Col>
                          <Col md={2}>
                            <input className="form-control" type="number" placeholder="순서" value={item.priority} onChange={(event) => updateItem(index, "priority", event.target.value)} />
                          </Col>
                          <Col md={2}>
                            <Button color="light" className="w-100" onClick={() => removeItem(index)}>삭제</Button>
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">상태</label>
                  <select className="form-select" value={form.state} onChange={(event) => setForm({ ...form, state: event.target.value })}>
                    <option value="active">활성</option>
                    <option value="paused">중지</option>
                  </select>
                </div>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label">링크 시작</label>
                      <input className="form-control" type="datetime-local" value={form.starts_at} onChange={(event) => setForm({ ...form, starts_at: event.target.value })} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label">링크 종료</label>
                      <input className="form-control" type="datetime-local" value={form.ends_at} onChange={(event) => setForm({ ...form, ends_at: event.target.value })} />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label">예약 시작일</label>
                      <input className="form-control" type="date" value={form.reservation_start_date} onChange={(event) => setForm({ ...form, reservation_start_date: event.target.value })} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label">예약 종료일</label>
                      <input className="form-control" type="date" value={form.reservation_end_date} onChange={(event) => setForm({ ...form, reservation_end_date: event.target.value })} />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label">전체 한도</label>
                      <input className="form-control" type="number" min={0} value={form.max_redemptions} onChange={(event) => setForm({ ...form, max_redemptions: event.target.value })} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label">고객별 한도</label>
                      <input className="form-control" type="number" min={0} value={form.per_user_limit} onChange={(event) => setForm({ ...form, per_user_limit: event.target.value })} />
                    </div>
                  </Col>
                </Row>
                <div className="mb-3">
                  <div className="form-check form-switch">
                    <input
                      id="private-offer-without-target"
                      className="form-check-input"
                      type="checkbox"
                      checked={form.allow_without_target}
                      onChange={(event) => setForm({ ...form, allow_without_target: event.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="private-offer-without-target">
                      대상 전화번호 없이 사용
                    </label>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">대상 전화번호</label>
                  <textarea
                    className="form-control"
                    rows={8}
                    value={form.target_phone_nums}
                    disabled={form.allow_without_target}
                    placeholder={"01012345678\n010-1234-5678\n010 1234 5678"}
                    onChange={(event) => setForm({ ...form, target_phone_nums: event.target.value })}
                  />
                  <div className="form-text">
                    한 줄에 한 명씩 입력하세요. 하이픈, 공백은 자동으로 제거되어 숫자만 저장됩니다. 대상 전화번호 없이 사용을 켜면 로그인 사용자는 누구나 링크로 구매할 수 있습니다.
                  </div>
                </div>
                {selectedOffer ? (
                  <div className="mb-3">
                    <label className="form-label">링크</label>
                    <div className="input-group">
                      <input className="form-control" readOnly value={buildOfferUrl(selectedOffer.token)} />
                      <Button color="secondary" onClick={() => copyOfferLink(selectedOffer.token)}>복사</Button>
                    </div>
                  </div>
                ) : null}
                <div className="d-flex justify-content-end gap-2">
                  <Button color="light" onClick={resetForm}>취소</Button>
                  <Button color="success" disabled={loading} onClick={saveOffer}>{loading ? "저장 중" : "저장"}</Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
